import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Alert,
  Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from '../../api/axios';

const POST_LOGIN = '/login';

export const Login = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const auth = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loginError, setLoginError] = useState(false);
  const [accountError, setAccountError] = useState("");

  useEffect(()=> {
    if(auth.loggedIn){
      navigate('/plans')
    }
  }, [auth.loggedIn, navigate])

  const handleSignIn = async () => {
    setEmailError('');
    setPasswordError('');
    setLoginError(false);

    let hasError = false;

    if (!email.trim()) {
      setEmailError('You must enter a registered email address.');
      hasError = true;
    }

    if (!password.trim()) {
      setPasswordError('You must enter a password.');
      hasError = true;
    }

    if (hasError) return;

    try {
  const response = await axios.post(POST_LOGIN, {
    email,
    password,
    deviceId: "web",
  });

  if (response.status === 200 && response.data?.code === 0) {
    auth.login(
      response.data.message.token.id,
      response.data.message.user.id,
      response.data.message.user.role,
      `${response.data.message.user.first} ${response.data.message.user.last}`,
      response.data.message.user.orgId
    );
    navigate("/plans");
  } else {
    // wrong email/password
    setLoginError("Email and password didn’t match");
    setAccountError("");
  }
} catch (error) {
  console.error("Login error:", error);

  if (error.response) {
    if (error.response.status === 428) {
      // inactive account
      setAccountError("Your account was deactivated");
    } else {
      setLoginError("Login failed. Please try again.");
    }
  } else {
    setLoginError("Network error. Please try again later.");
  }
}
  };

  const handleForgotPassword = async () => {
    navigate('/resetpass');    
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      {/* LEFT SIDE (only for non-mobile) */}
      {!isMobile && (
        <Box
          sx={{
            width: '50%',
            height: '100%',
            backgroundColor: '#42A5F5',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        />
      )}

      {/* RIGHT SIDE (form) */}
      <Box
        sx={{
          width: isMobile ? '100%' : '50%',
          height: '100%',
          backgroundColor: '#fff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          px: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 360,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <form
  onSubmit={(e) => {
    e.preventDefault(); // prevent page reload
    handleSignIn();
  }}
  style={{
    width: '100%',
    maxWidth: 360,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  }}
>
  {/* Logo */}
  <Typography
    variant="h3"
    sx={{
      textAlign: 'center',
      width: '100%',
      color: '#1565C0',
    }}
  >
    <img
      src="/vertical.svg"
      alt="Planmark by OcuBuild"
      style={{ width: '265px', display: 'block', margin: '0 auto' }}
    />
  </Typography>

  {/* Title */}
  <Typography variant="h4" gutterBottom>
    Sign in
  </Typography>

  {loginError && (
  <Alert severity="error" sx={{ mt: 2 }}>
    {loginError}
    <br />
    Please contact your administrator.
  </Alert>
)}

  {/* Error alert */}
  {/* {loginError && (
    <Alert severity="error" sx={{ width: '100%' }}>
      <Typography variant="subtitle2" fontWeight="bold">
        Email and password didn’t match
      </Typography>
      <Typography variant="body2">Please review and try again.</Typography>
    </Alert>
  )} */}

  {accountError && (
  <Alert severity="error" sx={{ width: "100%", mt: 2 }}>
    <Typography variant="subtitle2" fontWeight="bold">
      {accountError}
    </Typography>
    <Typography variant="body2">
      Please contact your administrator.
    </Typography>
  </Alert>
)}

  {/* Email Field */}
  <TextField
    fullWidth
    label="Email"
    variant="standard"
    value={email}
    onChange={(e) => {
      setEmail(e.target.value);
      setEmailError('');
      setLoginError(false);
    }}
    error={!!emailError || loginError}
    helperText={emailError}
    autoComplete="email"
    autoFocus
    onKeyDown={(e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        document.getElementById('password-field')?.focus();
      }
    }}
  />

  {/* Password Field */}
  <TextField
    fullWidth
    id="password-field"
    label="Password"
    type="password"
    variant="standard"
    value={password}
    onChange={(e) => {
      setPassword(e.target.value);
      setPasswordError('');
      setLoginError(false);
    }}
    error={!!passwordError || loginError}
    helperText={passwordError}
    autoComplete="current-password"
  />

  {/* Sign In + Forgot */}
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      mt: 2,
    }}
  >
    <Button
      variant="contained"
      size="medium"
      sx={{ height: 46 }}
      type="submit"
    >
      SIGN IN
    </Button>

    <Link
      variant="body2"
      underline="none"
      onClick={handleForgotPassword}
      sx={{
        color: theme.palette.grey[600],
        ml: 2,
        cursor: 'pointer',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
    >
      Forgot password?
    </Link>
  </Box>
</form>

          
        </Box>
      </Box>
    </Box>
  );
};
